import type { ProColumns } from '@ant-design/pro-table'
import ProTable from '@ant-design/pro-table'
import { Trans } from '@lingui/macro'
import { ConfigProvider, Skeleton } from 'antd'
import { SortOrder } from 'antd/es/table/interface'
import enUS from 'antd/locale/en_US'
import React, { useState } from 'react'
import { getItem } from './localStorage'

// just For showing columns example just uncomment the code below
// const proTableColumns: ProColumns= [
//     {
//         title: t`Greenlight ID`,
//         dataIndex: 'greenlightId',
//         key: 'greenlightId',
//         width: 100,
//         render: (_, patient) => <PatientIconResolver {...patient} />,
//     },
// ]

/**
 * Generate storage key for column state persistence
 * @param customKey - Optional custom storage key (used as-is if provided)
 * @returns Storage key string
 */
const generateStorageKey = (customKey?: string): string => {
    if (customKey) return customKey
    if (typeof window !== 'undefined') {
        return `omni-table-columns-${window.location.pathname}`
    }
    return 'omni-table-columns-default'
}

/**
 * Load column state from localStorage
 * @param storageKey - Optional custom storage key
 * @param enabled - Whether persistence is enabled
 * @returns Column state map or empty object
 */
const loadColumnStateFromStorage = (storageKey?: string, enabled: boolean = false): Record<string, { show?: boolean }> => {
    if (!enabled) return {}
    const key = generateStorageKey(storageKey)
    const savedState = getItem<Record<string, { show?: boolean }>>(key, {})
    return savedState || {}
}

/**
 * Save column state to localStorage
 * @param columnsState - Column state map to save
 * @param storageKey - Optional custom storage key
 * @param enabled - Whether persistence is enabled
 */

export interface OmniDataGridTableProps<T = any> {
    dataSource?: T[]
    columns: ProColumns<T>[]
    loading?: boolean
    onSortChange?: (sortString: string | null) => void // Changed: now returns formatted string like "first_name:asc"
    // Pagination props
    currentPage?: number
    pageSize?: number
    total?: number
    onPaginationChange?: (page: number, pageSize: number) => void
    // Table size/styling

    // Optional: Map camelCase field names to snake_case for API
    fieldNameMap?: Record<string, string>
    // Row key configuration - supports different key fields (id, uuid, key, _id, etc.)
    rowKey?: string | ((record: T) => string)
    // LocalStorage key for persisting column state - used as-is if provided, otherwise auto-generates from URL pathname
    storageKey?: string
    // Enable column state persistence to localStorage (default: false)
    enableColumnStatePersistence?: boolean
    // Row selection props
    enableRowSelection?: boolean
    // Callback when selected rows change - receives array of selected row keys and selected rows
    onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void
}

const OmniDataGridTable = <T extends Record<string, any> = any>({
    dataSource,
    columns,
    loading = false,
    onSortChange,
    currentPage = 1,
    pageSize = 10,
    total,
    onPaginationChange,

    fieldNameMap = {},
    rowKey = 'id',
    storageKey,
    enableColumnStatePersistence = false,
    enableRowSelection = false,
    onSelectionChange
}: OmniDataGridTableProps<T>) => {
    // Load initial column state from localStorage
    const [columnsStateMap, setColumnsStateMap] = useState<Record<string, { show?: boolean }>>(() =>
        loadColumnStateFromStorage(storageKey, enableColumnStatePersistence)
    )

    // Manage table density
    const [tableSize, setTableSize] = useState<'small' | 'middle' | 'large'>('middle')

    // Internal state for selected row keys
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

    // Handle row selection change
    const handleSelectionChange = (newSelectedRowKeys: React.Key[], selectedRows: T[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
        onSelectionChange?.(newSelectedRowKeys, selectedRows)
    }

    // Build row selection config
    const rowSelection = enableRowSelection
        ? {
              selectedRowKeys,
              onChange: handleSelectionChange
          }
        : undefined

    // Save column state to localStorage whenever it changes

    // Use provided total or calculate from dataSource
    const totalRecords = total !== undefined ? total : dataSource?.length || 0

    // Default scroll configuration
    const scrollY = 'calc(100vh - 350px)'
    const scrollX = 'max-content'

    // Convert camelCase to snake_case
    const camelToSnake = (str: string): string => {
        return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    }

    // Format sort string for API (e.g., "first_name:asc")
    const formatSortString = (field: string | null, order: SortOrder): string | null => {
        if (!field || !order) {
            return null
        }

        // Use fieldNameMap if provided, otherwise convert camelCase to snake_case
        const apiField = fieldNameMap[field] || camelToSnake(field)
        const apiOrder = order === 'ascend' ? 'asc' : 'desc'

        return `${apiField}:${apiOrder}`
    }

    // Handle table change (sorting, filtering, pagination)
    const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
        // Handle sorting
        if (onSortChange) {
            const sortString = formatSortString(sorter.field, sorter.order)
            onSortChange(sortString)
        }

        // Handle pagination,pagesizes,filters - always call the callback to ensure state sync
        if (onPaginationChange && pagination) {
            const newPage = pagination.current || 1
            const newPageSize = pagination.pageSize || 10
            onPaginationChange(newPage, newPageSize)
        }
    }

    // Generate skeleton data and columns for loading state
    const skeletonData = loading ? (Array.from({ length: pageSize }, () => ({})) as T[]) : dataSource

    const skeletonColumns: ProColumns<T>[] = loading
        ? columns.map((col) => ({
              ...col,
              render: () => <Skeleton.Input active style={{ width: '100%', height: '20px' }} />
          }))
        : columns

    return (
        <ConfigProvider locale={enUS}>
            <div className='w-full h-full'>
                <style>{`
                    .ant-pro-table-list-toolbar {
                        padding-top: 4px !important;
                        padding-bottom: 4px !important;
                    }
                    .ant-pro-table-list-toolbar-container {
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    }
                `}</style>
                <ProTable<T>
                    columns={skeletonColumns}
                    dataSource={skeletonData}
                    rowKey={rowKey}
                    loading={false}
                    rowSelection={rowSelection}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => (
                            <span>
                                {range[0]}-{range[1]} <Trans>of</Trans> {total} <Trans>items</Trans>
                            </span>
                        )
                    }}
                    size={tableSize}
                    search={false}
                    dateFormatter='string'
                    columnsState={{
                        value: columnsStateMap,
                        onChange: setColumnsStateMap,
                        ...(enableColumnStatePersistence && {
                            persistenceKey: storageKey,
                            persistenceType: 'localStorage'
                        })
                    }}
                    options={{
                        reload: false,
                        density: true,
                        setting: true,
                        fullScreen: true
                    }}
                    onSizeChange={(newSize) => newSize && setTableSize(newSize)}
                    scroll={{
                        x: scrollX,
                        y: scrollY
                    }}
                    sticky
                    onChange={handleTableChange}
                />
            </div>
        </ConfigProvider>
    )
}

export default OmniDataGridTable
export type { ProColumns } from '@ant-design/pro-table'
export type { SortOrder } from 'antd/es/table/interface'
